import React, { useEffect, useMemo, useCallback, RefObject, useState } from 'react';
import { Row, Col, Button, Dropdown, Avatar, Badge, Typography, Skeleton, notification, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'app/atoms';
import { PermissionActions, PermissionSubjects } from 'configs/permissionConstants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { endSession, removeTokenFromLocal } from 'app/modules/Auth/services';
import NotificationIcon from 'assets/icons/NotificationIcon';
import SignoutIcon from 'assets/icons/SignoutIcon';
import SettingsprofileIcon from 'assets/icons/SettingsprofileIcon';
import ChangepasswordIcon from 'assets/icons/ChangepasswordIcon';
import ArrowIcon from 'assets/icons/ArrowIcon';
import { ArrowLeftOutlined, ArrowRightOutlined, MenuUnfoldOutlined, MenuFoldOutlined } from '@ant-design/icons';
import { useLocation, useParams, Link, useNavigate } from 'react-router-dom';
import { auth } from 'app/modules/Auth/firebaseConfig';
import { getInitials } from 'utils/misc';
import './index.css';
import { ErrorMessage, getErrorMessage } from 'utils/axios';
import { Environment, REFRESH_INTERVAL, routeDisplayNames, useRTL } from 'configs/constants';
import { useUserInfo } from 'app/hooks/useUserInfo';
import modal from 'antd/es/modal';
import { LOGIN, PROFILE } from 'configs/routeNames';
import { getUnreadNotificationCount } from 'app/modules/Notification/services';
import { NotificationProps } from 'utils/types';
import { NOTIFICATION, ROOT } from 'configs/routeNames';
import OnboardingTour from 'app/modules/Onboarding/OnboardingTour';

const { Text } = Typography;

interface HeaderProps {
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  collapsed: boolean;
  selectedMenuItem: string;
  isMobile: boolean;
  setIsMobile: React.Dispatch<React.SetStateAction<boolean>>;
  onBack?: () => void;
  detailTitle?: string;
  menuButtonRef: RefObject<HTMLDivElement>;
}

interface IHeaderProps {
  selectedMenuItem: string;
  onBack?: () => void;
  detailTitle?: string;
  isLoading?: boolean;
}

// Memoize static icons
const MemoizedNotificationIcon = React.memo(NotificationIcon);
const MemoizedSettingsprofileIcon = React.memo(SettingsprofileIcon);
const MemoizedChangepasswordIcon = React.memo(ChangepasswordIcon);
const MemoizedSignoutIcon = React.memo(SignoutIcon);
const MemoizedArrowIcon = React.memo(ArrowIcon);

const HeaderWrap: React.FC<HeaderProps> = ({
  collapsed,
  setCollapsed,
  selectedMenuItem,
  isMobile,
  onBack,
  detailTitle,
  menuButtonRef,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const ability = useAbility(AbilityContext);

  const [startTour, setStartTour] = useState(false);
  const [isTourPaused, setIsTourPaused] = useState(false);
  const location = useLocation();
  const params = useParams();
  const isRTL = useRTL();
  const navigate = useNavigate();

  const arrowClassName =
    'size-4 lg:size-4 text-gray-650 font-bold [&>svg]:size-4 [&>svg]:lg:size-4 [&>svg]:stroke-[3px]';

  const getCurrentMenuItem = useCallback(() => {
    if (params['*']) {
      const pathParts = params['*'].split('/');
      const currentPath = pathParts[0];
      const basePath = currentPath === 'view' ? pathParts[1] : currentPath;
      return routeDisplayNames(basePath, t);
    }

    const path = location.pathname.split('/')[1];
    if (!path) return t('headers.home');

    return routeDisplayNames(path, t);
  }, [params, location.pathname, t]);

  // Memoize header props
  const headerPropsData = useMemo(
    () => ({
      selectedMenuItem: selectedMenuItem || getCurrentMenuItem(),
      onBack,
      detailTitle,
      isLoading: false,
    }),
    [selectedMenuItem, getCurrentMenuItem, onBack, detailTitle],
  );

  useEffect(() => {
    queryClient.setQueryData(['header-props'], headerPropsData);
  }, [queryClient, headerPropsData]);

  const { data: headerProps } = useQuery<IHeaderProps>({
    queryKey: ['header-props'],
    queryFn: () => headerPropsData,
    initialData: headerPropsData,
  });

  const endSessionApi = useMutation({
    mutationKey: ['endSession'],
    mutationFn: endSession,
    onSuccess: () => {
      removeTokenFromLocal();
    },
    onError: (error: ErrorMessage) => {
      notification.error({
        message: getErrorMessage(error),
        ...NotificationProps,
      });
    },
  });

  const {
    selectedMenuItem: currentMenuItem,
    onBack: onBackCallback,
    detailTitle: currentDetailTitle,
    isLoading,
  } = headerProps || {};

  const userInfoApi = useUserInfo();
  const userId = userInfoApi.data?.userId;

  const handleLogout = useCallback(() => {
    endSessionApi.mutate();
    // Invalidate all auth and onboarding related queries
    queryClient.removeQueries({ queryKey: ['auth', 'user-info'] });
    queryClient.removeQueries({ queryKey: ['onboarding-messages'] });
    // Reset query client to ensure clean state
    queryClient.resetQueries();
    auth.signOut();
    // Clear all local storage items
    localStorage.clear();
    navigate(`/${LOGIN}`, { replace: true, state: {} });
  }, [endSessionApi, queryClient]);

  // Add effect to handle tour state on user change
  useEffect(() => {
    if (userId && location.pathname === ROOT && userInfoApi.data?.termsAccepted) {
      setStartTour(true);
    } else {
      setStartTour(false);
    }
  }, [userId, location.pathname, userInfoApi.data?.termsAccepted]);

  const showLogoutConfirm = useCallback(() => {
    modal.confirm({
      title: t('header.signOutModal.title'),
      content: t('header.signOutModal.description'),
      okText: t('header.signOutModal.submitText'),
      cancelText: t('header.signOutModal.cancelText'),
      onOk: handleLogout,
      centered: true,
    });
  }, [handleLogout, t]);

  const userDropDownOnClick = useCallback(
    (info: { key: string }) => {
      if (info.key === 'Signout') {
        showLogoutConfirm();
      } else if (info.key === 'Profile Settings') {
        navigate(`${PROFILE}/setting`);
      } else if (info.key === 'Change Password') {
        navigate(`${PROFILE}/change-password`);
      }
    },
    [endSessionApi, queryClient, navigate],
  );

  // Memoize menu items
  const menu = useMemo<MenuProps['items']>(
    () => [
      {
        key: 'profile',
        label: (
          <div className="flex items-center py-4 px-4 border-b-2 border-gray-200 rounded-none overflow-hidden">
            <Avatar className="bg-pink-300 text-gray-900 font-medium size-14 me-3" src={userInfoApi.data?.photo}>
              {getInitials(userInfoApi.data?.fullName ?? t('N/A'))}
            </Avatar>
            <div className="max-w-44 overflow-hidden">
              <span className="font-bold text-black text-lg block line-clamp-2 text-ellipsis whitespace-nowrap">
                <Tooltip
                  title={`${!userInfoApi.data?.fullName && !userInfoApi.data?.surName ? t('N/A') : (userInfoApi.data?.fullName ?? '')} ${userInfoApi.data?.surName ?? ''}`}
                  placement="top"
                >
                  {`${!userInfoApi.data?.fullName && !userInfoApi.data?.surName ? t('N/A') : (userInfoApi.data?.fullName ?? '')} ${userInfoApi.data?.surName ?? ''}`}
                </Tooltip>
              </span>
              <span className="text-gray-600 text-sm block overflow-hidden text-ellipsis whitespace-nowrap" dir="auto">
                {userInfoApi.data?.email ?? t('N/A')}
              </span>
            </div>
          </div>
        ),
      },
      {
        key: 'Profile Settings',
        label: (
          <div className="flex items-center px-4 h-14 opacity-200">
            <MemoizedSettingsprofileIcon className="me-3" />
            <span className="h5-regular">{t('header.dropdown.profilesettings')}</span>
          </div>
        ),
      },
      {
        key: 'Change Password',
        label: (
          <div className="flex items-center px-4 h-14 opacity-200">
            <MemoizedChangepasswordIcon className="me-3" />
            <span className="h5-regular">{t('header.dropdown.changepassword')}</span>
          </div>
        ),
      },
      {
        key: 'Signout',
        label: (
          <div className="flex items-center px-4 h-14">
            <MemoizedSignoutIcon className="me-3" />
            <span className="h5-regular text-redShade-100">{t('header.dropdown.signout')}</span>
          </div>
        ),
      },
    ],
    [userInfoApi.data, t],
  );

  const handleTourEnd = useCallback(() => {
    setStartTour(false);
    setIsTourPaused(false);
  }, []);

  const handleMenuClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCollapsed(!collapsed);
    },
    [collapsed, setCollapsed],
  );

  // Memoize header content
  const headerContent = useMemo(() => {
    if (isLoading) {
      return <Skeleton.Input active size="small" className="!w-24 !h-5" />;
    }

    return (
      <>
        <Button
          type="text"
          icon={
            isRTL ? <ArrowRightOutlined className={arrowClassName} /> : <ArrowLeftOutlined className={arrowClassName} />
          }
          onClick={onBackCallback}
        />
        <Text className="app-header h5-medium !lg:pl-0 flex items-center gap-1 min-w-0">
          <span className="text-gray-650 ml-1 hidden lg:block truncate">{t(currentMenuItem)}</span>
          <span className="text-blackShade-200 hidden lg:block shrink-0">{currentDetailTitle && '>'}</span>
          <span className="text-blackShade-200 truncate">{currentDetailTitle}</span>
        </Text>
      </>
    );
  }, [isLoading, onBackCallback, currentMenuItem, currentDetailTitle, isRTL, t]);

  const { data: unreadCount } = useQuery({
    queryKey: ['unread-notification-count', userId],
    queryFn: () => getUnreadNotificationCount(userId || 0),
    enabled: !!userId,
    refetchInterval: REFRESH_INTERVAL,
  });

  return (
    <>
      <OnboardingTour
        start={startTour}
        setStartTour={setStartTour}
        onTourEnd={handleTourEnd}
        isPaused={isTourPaused}
        setIsTourPaused={setIsTourPaused}
      />

      <Row className="justify-between items-center w-full">
        <Col ref={menuButtonRef} className="menu-icon-button">
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={handleMenuClick}
          />
        </Col>

        <Col className="flex-1 min-w-0">
          {(() => {
            if (onBackCallback) {
              return <div className="flex items-center">{headerContent}</div>;
            }

            if (isLoading) {
              return (
                <div className="pl-2">
                  <Skeleton.Input active className="!w-32 !h-10" />
                </div>
              );
            }

            return (
              <Text className="app-header h5-medium pl-2 lg:pl-0 text-blackShade-200 truncate">
                {t(getCurrentMenuItem())}
              </Text>
            );
          })()}
        </Col>

        <Col>
          <Row className="items-center justify-end" gutter={10}>
            <Col>
              {import.meta.env.VITE_APP_MODE === Environment.STAGING ? (
                <div className="cursor-not-allowed opacity-50">
                  <Badge
                    count={unreadCount || 0}
                    color="blue"
                    offset={isRTL ? [8, 12] : [-8, 12]}
                    className="h-12 w-12 items-center justify-center flex"
                  >
                    <MemoizedNotificationIcon
                      data-testid="notification-icon"
                      className="size-6"
                      id="notification-selector"
                    />
                  </Badge>
                </div>
              ) : (
                <Link to={NOTIFICATION}>
                  <Badge
                    count={unreadCount || 0}
                    color="blue"
                    offset={isRTL ? [8, 12] : [-8, 12]}
                    className="h-12 w-12 items-center justify-center flex"
                  >
                    <MemoizedNotificationIcon
                      data-testid="notification-icon"
                      className="size-6"
                      id="notification-selector"
                    />
                  </Badge>
                </Link>
              )}
            </Col>
            {!ability.can(PermissionActions.MANAGE, PermissionSubjects.USER) ? (
              <Col>
                <Dropdown
                  overlayClassName="dropdown-container pb-2.5"
                  menu={{ items: menu, onClick: userDropDownOnClick }}
                  trigger={['click']}
                  placement="bottomRight"
                >
                  {userInfoApi?.isLoading ? (
                    <Button
                      className="btn-container pp-username pr-0 flex items-center gap-1 lg:gap-2 h-12"
                      type="text"
                    >
                      <Skeleton.Avatar active className="!w-8 !h-8" />
                      <Skeleton.Input active className=" !h-8" />
                    </Button>
                  ) : (
                    <Button
                      className="btn-container pp-username pr-0 flex items-center gap-1 lg:gap-2"
                      type="text"
                      data-testid="profile-dropdown-button"
                    >
                      <div className="flex items-center gap-1 lg:gap-2">
                        <Avatar className="bg-pink-300 text-gray-900 h5-medium" src={userInfoApi.data?.photo} size={32}>
                          {getInitials(userInfoApi.data?.fullName ?? t('N/A'))}
                        </Avatar>
                        {!isMobile && (
                          <span className="max-w-44">
                            <span className="overflow-hidden block text-ellipsis whitespace-nowrap">
                              <Tooltip
                                title={`${!userInfoApi.data?.fullName && !userInfoApi.data?.surName ? t('N/A') : (userInfoApi.data?.fullName ?? '')} ${userInfoApi.data?.surName ?? ''}`}
                                placement="top"
                              >
                                {`${!userInfoApi.data?.fullName && !userInfoApi.data?.surName ? t('N/A') : (userInfoApi.data?.fullName ?? '')} ${userInfoApi.data?.surName ?? ''}`}
                              </Tooltip>
                            </span>
                          </span>
                        )}
                      </div>
                      <MemoizedArrowIcon />
                    </Button>
                  )}
                </Dropdown>
              </Col>
            ) : null}
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default React.memo(HeaderWrap);
